import { css } from "dx-styles";
import type { ReactNode } from "react";
import { useCallback, useEffect, useState } from "react";
import { Link, Navigate, Route, Routes, useNavigate } from "react-router-dom";

import { ApiError, api } from "./api/client";
import { brand, chrome, layout, row } from "./components/primitives";
import { Account } from "./screens/Account";
import { Collections } from "./screens/Collections";
import { Editor } from "./screens/Editor";
import { Landing } from "./screens/Landing";
import { Library } from "./screens/Library";
import { Login } from "./screens/Login";
import { Privacy } from "./screens/Privacy";
import { Terms } from "./screens/Terms";
import { tokens } from "./theme/tokens";

const navLink = css({
  color: tokens.color.pencil,
  textDecoration: "none",
  fontWeight: 600,
  "&:hover": { color: tokens.color.ink },
});

const bar = css({
  maxWidth: "1100px",
  margin: "0 auto",
  padding: `${tokens.space.sm} ${tokens.space.lg}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
});

const footerBar = css({
  borderTop: `1px solid ${tokens.color.rule}`,
  marginBlockStart: tokens.space.xl,
  padding: `${tokens.space.md} ${tokens.space.lg}`,
  display: "flex",
  gap: tokens.space.md,
  flexWrap: "wrap",
  alignItems: "center",
  justifyContent: "center",
  color: tokens.color.pencil,
  fontSize: "13px",
});

/// On every page, including the landing page a subscriber arrives at: the two
/// documents have to be reachable from wherever somebody is standing.
function Footer() {
  return (
    <footer className={footerBar}>
      <span>Diditake.it</span>
      <Link to="/privacy" className={navLink}>
        Privacy
      </Link>
      <Link to="/terms" className={navLink}>
        Terms
      </Link>
    </footer>
  );
}

type Session = "unknown" | "signedIn" | "signedOut";

/** One probe decides which side of the app the user sees. */
function useSession(): [Session, (next: Session) => void] {
  const [session, setSession] = useState<Session>("unknown");
  useEffect(() => {
    void (async () => {
      try {
        await api.collections();
        setSession("signedIn");
      } catch (error) {
        setSession(error instanceof ApiError && error.status === 401 ? "signedOut" : "signedOut");
      }
    })();
  }, []);
  return [session, setSession];
}

/// One session probe for every authenticated screen, so a reload does not flash
/// the login page on the way to the collections list.
function Authenticated({ children }: { readonly children: (signedOut: () => void) => ReactNode }) {
  const [session, setSession] = useSession();
  const navigate = useNavigate();
  const signedOut = useCallback(() => {
    setSession("signedOut");
    navigate("/login", { replace: true });
  }, [navigate, setSession]);

  if (session === "unknown") return <main className={layout}>Loading…</main>;
  if (session === "signedOut") return <Navigate to="/login" replace />;
  return <>{children(signedOut)}</>;
}

export function App() {
  return (
    <>
      <header className={chrome}>
        <div className={bar}>
          <Link to="/" className={brand}>
            Diditake.it
          </Link>
          <nav className={row} aria-label="Main">
            <Link to="/" className={navLink}>
              Collections
            </Link>
            <Link to="/library" className={navLink}>
              Library
            </Link>
            <Link to="/account" className={navLink}>
              Settings
            </Link>
          </nav>
        </div>
      </header>
      <Routes>
        <Route
          path="/"
          element={<Authenticated>{(signedOut) => <Collections onSignedOut={signedOut} />}</Authenticated>}
        />
        <Route
          path="/collections/:collectionId"
          element={<Authenticated>{(signedOut) => <Editor onSignedOut={signedOut} />}</Authenticated>}
        />
        <Route
          path="/library"
          element={<Authenticated>{(signedOut) => <Library onSignedOut={signedOut} />}</Authenticated>}
        />
        <Route
          path="/account"
          element={<Authenticated>{(signedOut) => <Account onSignedOut={signedOut} />}</Authenticated>}
        />
        <Route path="/login" element={<Login />} />
        <Route path="/c/:publicId" element={<Landing />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Footer />
    </>
  );
}
